
/**
 * Ficoo Smart Connector checkout handler.
 *
 * @package    Ficoo_Smart_Connector_Core
 * @subpackage Ficoo_Smart_Connector_Core/public
 */

import { getSetting } from "@woocommerce/settings";
import { extensionCartUpdate } from "@woocommerce/blocks-checkout";
import { addAction, addFilter } from "@wordpress/hooks";
import { dispatch, select, subscribe, useSelect, useDispatch } from "@wordpress/data";
import { cartStore, checkoutStore } from "@woocommerce/block-data";
import { createRef, useEffect } from "@wordpress/element";
import { useState } from "@wordpress/element";
import apiFetch from "@wordpress/api-fetch";
import { SelectControl, TextareaControl, CheckboxControl } from "@wordpress/components";
import { __, _x, _n, sprintf } from "@wordpress/i18n";
import { debounce, remove } from "lodash";

// Internal dependencies.
import { ficooInput, ficooDiv, ficooShippingAddressDiv, ficooBillingAddressDiv, SameAddress, woocommerceFields, billingCountry, shippingCountry } from "./fields-selectors.js";
import { logger, ficooData } from "./logger.js";
// import { validateVatFormat, validateItalianTaxCode, validateEmail, validateRecCode } from "./validator.js";
import {
  validateVatFormat as coreValidateVatFormat,
  validateItalianTaxCode as coreValidateItalianTaxCode,
  validateEmail,
  validateRecCode
} from "./validator.js";

import {
  callForEmptyVat as coreCallForEmptyVat,
  callForValidVat as coreCallForValidVat,
  callForInvalidVat as coreCallForInvalidVat
} from "./ajax.js";

import {
  showSuccessMessage,
  showErrorMessage,
  removeErrorMessage,
  removeSuccessMessage,
  showSpinner,
  removeSpinner,
} from "./errors.js";

/**
 * Main checkout handler class.
 */
class FicooCheckoutHandler {
  /**
   * Constructor.
   */
  constructor() {
    // Configuration options.
    this.config = {
      section: ficooData.options.section,
      useShippingCountry: ficooData.options.use_shipping_country,
      enableVatRegexVal: ficooData.options.enable_vat_regex_validation,
      enableVatExemption: ficooData.options.enable_vat_exemption,
      validateTaxcode: ficooData.options.validate_taxcode,
      mandatoryInvoice: ficooData.options.mandatory_invoice,
      mandatoryVatNumber: ficooData.options.mandatory_vat_number,
      mandatoryTaxCode: ficooData.options.mandatory_tax_code,
      enableStyle: ficooData.options.enable_style,
    };

    // DOM elements cache.
    this.elements = {
      targetCountry: null,
      sameAddressCheckbox: null,
      sameAddressLabel: null,
      ficooIWantInvoiceCheckbox: null
    };

    // Debounce timer.
    this.debounceTimer = null;

    // Initialize on window load.
    this.init();
  }

  /**
   * Initialize the handler with DOM observer.
   */
  init() {
    // Instead of waiting for window load, wait for DOM to be ready and then observe for country elements.
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.startInitialization());
    } else {
      this.startInitialization();
    }
  }

  /**
   * Start initialization process.
   */
  startInitialization() {
    this.logConfiguration();
    window.ficooSection = this.config.section;
    this.waitForCountryElements();
  }

  /**
   * Wait for country elements to be available in the DOM.
   */
  waitForCountryElements() {
    const maxAttempts = 20;
    let attempts = 0;

    const checkElements = () => {
      attempts++;

      const billingCountryElement = document.getElementById('billing-country');
      const shippingCountryElement = document.getElementById('shipping-country');

      logger.info(`Attempt ${attempts}: Checking for country elements`);
      logger.p("Billing country found:", !!billingCountryElement);
      logger.p("Shipping country found:", !!shippingCountryElement);

      // Check if at least one country element is available.
      if (billingCountryElement || shippingCountryElement) {
        logger.info("Country elements found, proceeding with initialization");
        this.proceedWithInitialization();
        return;
      }

      // If not found and we haven't reached max attempts, try again.
      if (attempts < maxAttempts) {
        logger.info(`Country elements not found, retrying in 500ms (attempt ${attempts}/${maxAttempts})`);
        setTimeout(checkElements, 500);
      } else {
        logger.warn("Country elements not found after maximum attempts, proceeding anyway");
        this.proceedWithInitialization();
      }
    };

    checkElements();
  }

  /**
   * Proceed with full initialization once country elements are available.
   */
  proceedWithInitialization() {
    this.cacheElements();
    this.setupInitialState();
    this.setupEventListeners();
  }

  /**
   * Setup initial state.
   */
  setupInitialState() {
    // Get target country now that elements should be available.
    window.targetCountry = this.getTargetCountry();

    if (!window.targetCountry) {
      logger.warn("Target country still not found, will retry on first change event");
    }

    if ('other_section' === this.config.section) {
      this.handleFieldsVisibility(window.targetCountry);
    }

    if ('address_section' === this.config.section) {
      this.handleFieldsVisibilityShipping(window.targetCountry);
    }

    this.validateVatNumberOnLoad();
  }

  /**
   * Get target country based on configuration - simplified version.
   * @returns {HTMLElement|null} The target country select element.
   */
  getTargetCountry() {
    // Direct access to DOM elements.
    const billingCountryElement = document.getElementById('billing-country');
    const shippingCountryElement = document.getElementById('shipping-country');

    logger.p("Direct check - Billing country element:", !!billingCountryElement);
    logger.p("Direct check - Shipping country element:", !!shippingCountryElement);

    if ("1" === this.config.useShippingCountry) {
      // Priority to shipping country.
      if (shippingCountryElement) {
        logger.info("Using shipping country as target (priority setting)");
        logger.info("Target set to " + shippingCountryElement.id + " = " + shippingCountryElement.value);
        return shippingCountryElement;
      } else if (billingCountryElement) {
        logger.info("Using billing country as fallback");
        logger.info("Target set to " + billingCountryElement.id + " = " + billingCountryElement.value);
        return billingCountryElement;
      }
    } else {
      // Priority to billing country.
      if (billingCountryElement) {
        logger.info("Using billing country as target (default setting)");
        logger.info("Target set to " + billingCountryElement.id + " = " + billingCountryElement.value);
        return billingCountryElement;
      } else if (shippingCountryElement) {
        logger.info("Using shipping country as fallback");
        logger.info("Target set to " + shippingCountryElement.id + " = " + shippingCountryElement.value);
        return shippingCountryElement;
      }
    }

    logger.warn("No target country element found");
    return null;
  }

  /**
   * Enhanced handleChange with target country retry.
   */
  async handleChange(event) {
    // If target country is null, try to get it again.
    if (!window.targetCountry) {
      logger.info("Target country null, attempting to get it again");
      window.targetCountry = this.getTargetCountry();
    }

    const sameAddressCheckboxChanged = event.target.type === "checkbox" && event.target === this.elements.sameAddressCheckbox;
    const ficooIWantInvoiceCheckboxChanged = event.target.type === "checkbox" && event.target === this.elements.ficooIWantInvoiceCheckbox;

    // Log checkbox changes exactly as original.
    if (sameAddressCheckboxChanged || ficooIWantInvoiceCheckboxChanged) {
      if (event.target === this.elements.sameAddressCheckbox) {
        if (event.target.checked) {
          logger.info("Checked Same address checkbox");
        } else {
          logger.info("Unchecked Same address checkbox");
        }
      } else if (event.target === this.elements.ficooIWantInvoiceCheckbox) {
        logger.info("I need invoice ticked: " + event.target.checked);
      }
    }

    const isRelevantCountryChange =
      (event.target.id === "billing-country" && window.targetCountry?.id === "billing-country") ||
      (event.target.id === "shipping-country" && window.targetCountry?.id === "shipping-country");

    if (isRelevantCountryChange) {
      if (event.target.id === "billing-country") {
        logger.info("Billing country changed: " + event.target.value);
      } else if (event.target.id === "shipping-country") {
        logger.info("Shipping country changed: " + event.target.value);
      }
    }

    // Update target country only for relevant changes.
    if (isRelevantCountryChange || sameAddressCheckboxChanged || ficooIWantInvoiceCheckboxChanged) {
      const newTargetCountry = this.getTargetCountry();
      if (!newTargetCountry) {
        logger.warn("Target country non trovato dopo change event");
        return;
      }
      window.targetCountry = newTargetCountry;
    }

    // Launch only if checkbox changed.
    if (sameAddressCheckboxChanged) {
      this.handleFieldsVisibilityShipping(window.targetCountry);
    }

    const isRelevantVatChange = event.target.id === ficooInput.vat?.id;
    const isRelevantTaxChange = event.target.id === ficooInput.taxcode?.id;
    const isRelevantEmailPecChange = event.target.id === ficooInput.emailpec?.id;
    const isRelevantRecCodeChange = event.target.id === ficooInput.reccode?.id;

    // Stop if not a relevant event.
    if (!(sameAddressCheckboxChanged || ficooIWantInvoiceCheckboxChanged || isRelevantCountryChange ||
      isRelevantVatChange || isRelevantTaxChange || isRelevantEmailPecChange || isRelevantRecCodeChange)) {
      return;
    }

    logger.info(`Cambio rilevante rilevato: ${event.target.id || "checkbox"}`);

    // Handle fields visibility (remains the same).
    if ('other_section' === this.config.section) {
      this.handleFieldsVisibility(window.targetCountry);
    }


    // VAT number validation
    if (isRelevantVatChange) {
      return await this.handleValidationVat();
    }

    // Tax code validation
    if (isRelevantTaxChange) {
      return await this.handleValidationTaxCode();
    }

    // PEC number validation 
    if (isRelevantEmailPecChange) {
      return await this.handleValidationEmailPec();
    }

    // Reccode number validation
    if (isRelevantRecCodeChange) {
      return await this.handleValidationRecCode();
    }

    // Handle common validations if for example checkbox changed or country changed.
    if (sameAddressCheckboxChanged || ficooIWantInvoiceCheckboxChanged || isRelevantCountryChange) {
      await this.handleValidationTaxCode();
      await this.handleValidationEmailPec();
      await this.handleValidationRecCode();
      await this.handleValidationVat();
      return;
    }

  }

  /**
   * Log configuration for debugging.
   */
  logConfiguration() {
    logger.info("[Ficoo settings]");
    logger.p("Section in use: " + this.config.section);
    logger.p("Use shipping country? " + this.config.useShippingCountry);
    logger.p("Vat Validation enabled? " + this.config.enableVatRegexVal);
    logger.p("Vat exemption enabled " + this.config.enableVatExemption);
    logger.p("Validate tax code enabled? " + this.config.validateTaxcode);
    logger.p("Mandatory Invoice? " + this.config.mandatoryInvoice);
    logger.p("Mandatory Vat Number? " + this.config.mandatoryVatNumber);
    logger.p("Mandatory Tax Code? " + this.config.mandatoryTaxCode);
  }


  cacheElements() {
    const maxRetries = 5;
    let retryCount = 0;

    const tryCache = () => {
      retryCount++;

      if (SameAddress && SameAddress.checkbox) {
        this.elements.sameAddressCheckbox = SameAddress.checkbox;
      }

      if (SameAddress && SameAddress.label) {
        this.elements.sameAddressLabel = SameAddress.label;
      }

      if (ficooInput && ficooInput.checkbox) {
        this.elements.ficooIWantInvoiceCheckbox = ficooInput.checkbox;
        this.config.enableStyle ? this.addCheckboxStyle() : null;
      }

      // Check if we need to retry.
      const needsRetry = !this.elements.sameAddressCheckbox ||
        !this.elements.sameAddressLabel ||
        !this.elements.ficooIWantInvoiceCheckbox;

      if (needsRetry && retryCount < maxRetries) {
        logger.info(`Retrying element caching (attempt ${retryCount})`);
        setTimeout(tryCache, 1000);
      } else {
        this.logCachedElements();
      }
    };

    tryCache();
  }

  logCachedElements() {
    logger.info("Elements cached:");
    logger.p("Usa Stesso Indirizzo - Checkbox:", this.elements.sameAddressCheckbox);
    logger.p("Usa Stesso Indirizzo - Label:", this.elements.sameAddressLabel);
    logger.p("Checkbox ficoo voglio fattura:", this.elements.ficooIWantInvoiceCheckbox);
  }

  /**
   * Setup event listeners.
   */
  setupEventListeners() {
    // Main change event listener.
    document.addEventListener("change", (event) => this.handleChange(event));

    // Setup submit button validation.
    this.setupSubmitValidation();
  }


  /**
   * Handle validation for tax code.
   * @returns {Promise<boolean>} true if tax code is valid or empty, false if not valid.
   */
  async handleValidationTaxCode() {

    if ("1" !== this.config.validateTaxcode) {
      logger.info("Salto validazione codice fiscale per opzione disabilitata");
      return;
    }

    logger.info("START Validazione codice fiscale");
    const TaxCode = ficooInput.taxcode.value;

    if ("" !== TaxCode) {
      const countryValue = window.targetCountry?.value || '';

      if (TaxCode && countryValue === 'IT') {
        const isValid = this.validateItalianTaxCode(TaxCode, countryValue);

        if (!isValid) {
          removeErrorMessage("taxcode");
          removeSuccessMessage("taxcode");
          showErrorMessage(__("Invalid tax code", "ficoo-smart-connector-core"), "taxcode");
          logger.p(`Codice fiscale non valido: ${TaxCode}`);
          logger.info("--- END Validazione codice fiscale  --- ");
        } else {
          removeErrorMessage("taxcode");
        }
      } else {
        logger.p("Salto validazione codice fiscale per paese non ITA");
        removeErrorMessage("taxcode");
      }
    } else {
      logger.p("Salto validazione codice fiscale per campo vuoto");
      removeErrorMessage("taxcode");
    }
    logger.info("END Validazione codice fiscale");
    return;
  }

  // /**
  //  * Handle validation for vat number.
  //  * @returns {Promise<boolean>} true if vat number is valid or empty, false if not valid.
  //  */
  // async handleValidationVat() {

  //   logger.info("START Validazione partita iva IN LOCALE");
  //   const vatNumber = ficooInput.vat.value;

  //   // If not empty validate format.
  //   if ("" !== vatNumber) {
  //     const countryCode = window.targetCountry?.value || "";
  //     const isVatValid = validateVatFormat(vatNumber, countryCode);

  //     if (!isVatValid) {
  //       removeErrorMessage("vat");
  //       removeSuccessMessage("vat");
  //       showErrorMessage(__("The VAT number format is not valid for the country", "ficoo-smart-connector-core") + " " + countryCode, "vat");
  //       logger.p(`Formato partita IVA non valido per il paese ${countryCode}: ${vatNumber}`);
  //     } else {
  //       removeErrorMessage("vat");
  //     }
  //   } else {
  //     removeErrorMessage("vat");
  //   }
  //   logger.info("END Validazione partita iva IN LOCALE");

  // }

  /**
 * Handle validation for vat number.
 * @returns {Promise<boolean>} true if vat number is valid or empty, false if not valid.
 */
  async handleValidationVat() {

    logger.info("START handleValidationVat");
    const vatNumber = ficooInput.vat.value;
    const countryCode = window.targetCountry?.value || "";
    const enableRegex = "1" === this.config.enableVatRegexVal;

    // remove errors if they are already set.
    removeSpinner("vat");
    removeErrorMessage("vat");
    removeSuccessMessage("vat");
    removeErrorMessage("woototalwrap");
    removeSuccessMessage("woototalwrap");

    // vatNumber can be empty or not empty.
    // if empty make shiure to send empty to server.
    if ("" === vatNumber) {
      logger.p(`Vat number vuoto, invio richiesta al server con "" per paese: ${countryCode}`);
      return await this.handleVatAjaxCalls('empty');

    } else if ("" !== vatNumber && enableRegex) {
      // if not empty check if enableregex is enabled.
      logger.p("Vat number non vuoto, REGEX abilitata");
      let isVatValid = this.validateVatFormat(vatNumber, countryCode);
      if (isVatValid) {
        logger.p("Formato partita IVA valido per REGEX invio richesta al server 'valid' per il paese: " + countryCode);
        return await this.handleVatAjaxCalls('valid', vatNumber, countryCode);
      }
      else {
        logger.p("Formato partita IVA nonvalido per REGEX invio richesta al server 'invalid' per il paese: " + countryCode);
        showErrorMessage(__("The VAT number format is not valid for the country", "ficoo-smart-connector-core") + " " + countryCode, "vat");
        logger.p(`Formato partita IVA non valido per il paese ${countryCode}: ${vatNumber}`);
        // Send invalid to server.
        return await this.handleVatAjaxCalls('invalid');
      }

    } else if ("" !== vatNumber && !enableRegex) {
      logger.p("Vat number non vuoto, REGEX NON abilitata, considero valido ed invio al server 'valid'  per il paese: " + countryCode);
      // if not empty and regex is not enabled, consider valid.
      return await this.handleVatAjaxCalls('valid', vatNumber, countryCode);

    } else {
      logger.error("Stato non previsto in handleValidationVat");
      return false;
    }
  }

  /**
   * Handle validation for email PEC.
   * @returns {Promise<boolean>} true if email PEC is valid or empty, false if not valid.
   */
  async handleValidationEmailPec() {

    const EmailPec = ficooInput.emailpec.value;

    if ("" !== EmailPec) {
      const isValid = validateEmail(EmailPec);

      if (!isValid) {
        removeErrorMessage("pec");
        removeSuccessMessage("pec");
        showErrorMessage(__("Invalid PEC address", "ficoo-smart-connector-core"), "pec");
        logger.p(`indirizzo pec non valido ${EmailPec}`);
        return;
      } else {
        removeErrorMessage("pec");
        // showSuccessMessage("Codice fiscale valido", "taxcode", 5);
      }

    } else {
      removeErrorMessage("pec");
    }

    return;

  }

  /**
   * Handle validation for recipient code.
   * @returns {Promise<boolean>} true if recipient code is valid or empty, false if not valid.
   */
  async handleValidationRecCode() {

    const RecCode = ficooInput.reccode.value;

    if ("" !== RecCode) {

      const isValid = validateRecCode(RecCode);

      if (!isValid) {
        removeErrorMessage("reccode");
        removeSuccessMessage("reccode");
        showErrorMessage(__("Invalid recipient code", "ficoo-smart-connector-core"), "reccode");
        logger.p(`Codice destinatario non valido, 6 o 7 caratteri. ${RecCode}`);
        return;
      } else {
        removeErrorMessage("reccode");
        // showSuccessMessage("Codice fiscale valido", "taxcode", 5);
      }

    } else {
      removeErrorMessage("reccode");
    }

    return;


  }

  /**
   * Check if Pro validator is available, otherwise use core functions.
   * @param {*} vatNumber 
   * @param {*} country 
   * @returns 
   */
  validateVatFormat(vatNumber, country) {
    if (typeof window.FicooProValidator !== 'undefined' &&
      typeof window.FicooProValidator.proValidateVatFormat === 'function') {
      return window.FicooProValidator.proValidateVatFormat(vatNumber, country);
    }
    return coreValidateVatFormat(vatNumber, country);
  }

  /**
   *  Check if Pro validator is available, otherwise use core functions.
   * @param {*} value 
   * @param {*} country 
   * @returns 
   */
  validateItalianTaxCode(value, country) {
    if (typeof window.FicooProValidator !== 'undefined' &&
      typeof window.FicooProValidator.proValidateItalianTaxCode === 'function') {
      return window.FicooProValidator.proValidateItalianTaxCode(value, country);
    }
    return coreValidateItalianTaxCode(value, country);
  }

  /**
   * Used only for OTHER SECTION
   * Handle fields visibility based on country and checkbox state.
   * Fields in address section are managed by woocommerce.
   */
  handleFieldsVisibility(targetCountry) {
    logger.info("handleFieldsVisibility START.");
    const ficooIWantInvoiceCheckbox = this.elements.ficooIWantInvoiceCheckbox;

    if (!ficooIWantInvoiceCheckbox) {
      logger.warn("Checkbox non trovato");
      logger.info("handleFieldsVisibility END.");
      return;
    }

    // Don't execute if checkbox doesn't exist (address).
    if ('fake-checkbox' === ficooIWantInvoiceCheckbox.id && '1' === this.config.mandatoryInvoice) {
      logger.info("Checkbox fittizia trovata...");
      this.insertTitleDiv();
      return;
    }

    if (!targetCountry) {
      logger.warn("Target country non trovato avviso 2");
      logger.info("handleFieldsVisibility END.");
      return;
    }

    this.hideFicooFields();
    this.removeTitleDiv();

    if (ficooIWantInvoiceCheckbox.checked) {
      if ("IT" === targetCountry.value) {
        this.showFicooFields();
      } else {
        this.showFicooFields("vat", "taxcode");
      }
      this.insertTitleDiv();

    }

    logger.info("handleFieldsVisibility END.");
  }

  /**
   * Used only for ADDRESS SECTION
   * Handle shipping fields visibility.
   * 
   */
  handleFieldsVisibilityShipping(targetCountry) {
    logger.info("handleFieldsVisibilityShipping START.");
    if (!targetCountry) {
      logger.warn("Target country non trovato avviso 3");
      logger.info("handleFieldsVisibilityShipping END.");
      return;
    }
    const sameAddressCheckbox = this.elements.sameAddressCheckbox;
    const sameAddressLabel = this.elements.sameAddressLabel;

    if (sameAddressCheckbox.checked) {
      // Add CSS to label.
      if (sameAddressLabel) {
        // sameAddressLabel.style.background = "antiquewhite";
        sameAddressLabel.style.background = "color-mix(in srgb,currentColor 6%,transparent)";

        sameAddressLabel.style.fontSize = "medium";
      }
    } else {
      // Add CSS to label.
      if (sameAddressLabel) {
        sameAddressLabel.style.background = "white";
        sameAddressLabel.style.fontSize = "medium";
      }
    }

    // First remove span if exists.
    this.removeTitleDiv();
    this.insertTitleDiv();


    // hide or show shipping fields.
    if ('shipping-country' === targetCountry.id) {

      this.showFicooShippingFields();
      removeErrorMessage("vatBilling");
      removeErrorMessage("taxcodeBilling");
      removeErrorMessage("pecBilling");
      removeErrorMessage("reccodeBilling");
      removeSuccessMessage("vatBilling");
      removeSuccessMessage("taxcodeBilling");
      this.handleValidationTaxCode();
      this.handleValidationVat();
      this.handleValidationEmailPec();
      this.handleValidationRecCode();
    } else if ('billing-country' === targetCountry.id) {
      this.hideFicooShippingFields();
      removeErrorMessage("vatShipping");
      removeErrorMessage("taxcodeShipping");
      removeErrorMessage("pecShipping");
      removeErrorMessage("reccodeShipping");
      removeSuccessMessage("vatShipping");
      removeSuccessMessage("taxcodeShipping");
      this.handleValidationTaxCode();
      this.handleValidationVat();
      this.handleValidationEmailPec();
      this.handleValidationRecCode();
    }
  }

  /**
   * Remove existing title div if any.
   */
  removeTitleDiv() {
    const existingSpan = document.querySelector(".ficoo-title-div");
    if (existingSpan) {
      existingSpan.remove();
    }
  }

  addCheckboxStyle() {
    // add style to checkbox div
    if (ficooDiv.checkbox) {
      ficooDiv.checkbox.style.borderRadius = "4px";
      ficooDiv.checkbox.style.padding = "1em";
      ficooDiv.checkbox.style.boxShadow = "inset 0 0 0 1.5px currentColor";
    }
  }

  /**
   *  Create and insert title div before VAT field.
   */
  insertTitleDiv() {

    this.removeTitleDiv();

    const section = this.config.section;
    // First remove span if exists.

    if ('address_section' === section && ficooDiv.vat && (ficooDiv.vat.parentNode.id === 'billing' || ficooDiv.vat.parentNode.id === 'shipping')) {

      const divContainer = document.createElement("div");
      divContainer.className = "ficoo-title-div"
      divContainer.style.cursor = "default"; // No pointer cursor.

      divContainer.style.marginTop = "2em";
      divContainer.style.flex = "0 0 100%"; // Full width

      const titleText = '1' === this.config.mandatoryInvoice ? __("Invoicing is mandatory", "ficoo-smart-connector-core") : __("Would you like an invoice?", "ficoo-smart-connector-core");
      const subtitle = '1' === this.config.mandatoryInvoice ? __("Fill in informations, mandatory fields are marked.", "ficoo-smart-connector-core") : __("If you wish, fill in the billing fields.", "ficoo-smart-connector-core");


      // create Title
      const h2Title = document.createElement("h2");
      h2Title.className = "wc-block-components-title";
      h2Title.tabIndex = 0;
      h2Title.innerText = titleText;


      // create subtext
      const span = document.createElement("span");
      span.className = "";
      span.tabIndex = 0;
      span.innerText = subtitle;
      span.style.fontSize = ".875em";
      span.style.lineHeight = "1.2";
      span.style.margin = "0 0 16px";

      divContainer.appendChild(h2Title);
      divContainer.appendChild(span);

      ficooDiv.vat.parentNode.insertBefore(divContainer, ficooDiv.vat);
    }

    if ('other_section' === section && ficooDiv.vat) {
      const divContainer = document.createElement("div");
      divContainer.className = "ficoo-title-div"
      divContainer.style.cursor = "default"; // No pointer cursor.
      section === 'address_section' ? divContainer.style.marginTop = "0.5em" : divContainer.style.marginTop = "1em";
      divContainer.style.flex = "0 0 100%"; // Full width

      const titleText = '1' === this.config.mandatoryInvoice ? __("Invoicing is mandatory", "ficoo-smart-connector-core") : '';
      const subtitle = '1' === this.config.mandatoryInvoice ? __("Fill in informations, mandatory fields are marked.", "ficoo-smart-connector-core") : __("If you wish, fill in the billing fields.", "ficoo-smart-connector-core");


      // create Title
      const h2Title = document.createElement("h2");
      h2Title.className = "wc-block-components-title";
      h2Title.tabIndex = 0;
      h2Title.innerText = titleText;

      section === 'address_section' ? h2Title.style.fontSize = "1em" : h2Title.style.fontSize = "inherit";


      // create subtex
      const span = document.createElement("span");
      span.className = "";
      span.tabIndex = 0;
      span.innerText = subtitle;
      span.style.fontSize = ".875em";
      span.style.lineHeight = "1.2";
      span.style.margin = "0 0 16px";

      divContainer.appendChild(h2Title);
      divContainer.appendChild(span);

      ficooDiv.vat.parentNode.insertBefore(divContainer, ficooDiv.vat);

    }
  }


  hideFicooFields(...fieldsToHide) {
    if (fieldsToHide.length === 0) {
      // If no parameters, hide all except checkbox.
      Object.values(ficooDiv).forEach((element) => {
        if (element && element.className.includes("block_billing_ficoo_invoice_requested")) {
          element.style.display = "block";
        } else if (element) {
          element.style.display = "none";
        }
      });
    } else {
      // Hide only specified fields.
      fieldsToHide.forEach((field) => {
        if (ficooDiv[field]) {
          ficooDiv[field].style.display = "none";
        }
      });
    }
  }

  /**
   * Show Ficoo fields.
   */
  showFicooFields(...fieldsToshow) {
    if (fieldsToshow.length === 0) {
      // If no parameters, show all.
      Object.values(ficooDiv).forEach((element) => {
        if (element) element.style.display = "block";
      });
    } else {
      // Show only specified fields.
      fieldsToshow.forEach((field) => {
        if (ficooDiv[field]) {
          ficooDiv[field].style.display = "block";
        }
      });
    }
  }

  /**
   * Hide shipping Ficoo fields.
   */
  hideFicooShippingFields() {
    // If no parameters, hide all.
    Object.values(ficooShippingAddressDiv).forEach((element) => {
      if (element) element.style.display = "none";
    });
  }

  /**
   * Show shipping Ficoo fields.
   */
  showFicooShippingFields() {
    // If no parameters, show all.
    Object.values(ficooShippingAddressDiv).forEach((element) => {
      if (element) element.style.display = "block";
    });
  }

  /**
   * Handle VAT validation AJAX requests
   * @param {boolean} precheck resulted by js validator.
   * @param {string|null} vatNumber optional, if null takes from field.
   * @param {string|null} countryCode optional, if null takes from target country.
   * @returns {Promise<boolean>} true if vat is valid, false if not valid or error.
   */
  /**
   * Trigger extensionCartUpdate while protecting address fields from being wiped.
   * WC Blocks checks localStorage WOOCOMMERCE_CHECKOUT_IS_CUSTOMER_DATA_DIRTY
   * synchronously before applying the server response. When "true", it strips
   * billing_address and shipping_address from the response, preserving form values.
   * For new customers the flag may not be set yet (300ms debounce), so we set it
   * explicitly before every cart update call.
   */
  refreshCart_old() {
    window.localStorage.setItem('WOOCOMMERCE_CHECKOUT_IS_CUSTOMER_DATA_DIRTY', 'true');
    const { extensionCartUpdate } = wc.blocksCheckout;
    extensionCartUpdate({
      namespace: "ficoo-smart-connector-core",
      data: {},
    });
  }

  /**
   * Same as refreshCart but uses the official WC Blocks API parameter
   * overwriteDirtyCustomerData: false to protect address fields.
   * No localStorage hack needed — WC strips billing/shipping from
   * the server response before merging into the Redux store.
   */
  refreshCart() {
    dispatch(cartStore).applyExtensionCartUpdate({
      namespace: "ficoo-smart-connector-core",
      data: {},
      overwriteDirtyCustomerData: false,
    });
  }

  async handleVatAjaxCalls(precheck = 'valid', vatNumber = null, countryCode = null) {

    clearTimeout(this.debounceTimer);
    let result = false;

    if (precheck === 'invalid') {
      this.debounceTimer = setTimeout(async () => {
        result = await this.callForInvalidVat();
        this.refreshCart();
      }, 2000);
      return result;
    }
    if (precheck === 'empty') {
      this.debounceTimer = setTimeout(async () => {
        result = await this.callForEmptyVat();
        this.refreshCart();
      }, 2000);
      return result;
    }

    vatNumber = vatNumber || ficooInput.vat.value;
    countryCode = countryCode || window.targetCountry?.value || "";
    showSpinner("vat");
    this.debounceTimer = setTimeout(async () => {
      result = await this.callForValidVat(vatNumber, countryCode, this.config.section, this.elements.ficooIWantInvoiceCheckbox);
      this.refreshCart();
    }, 2000);
    return result;
  }


  /**
   *  Check if Pro AJAX is available, otherwise use core functions.
   * @param {*} vatNumber 
   * @param {*} countryCode 
   */
  async callForValidVat(vatNumber, countryCode, section, ficooCheckbox) {
    if (typeof window.FicooProAjax !== 'undefined' &&
      typeof window.FicooProAjax.proCallForValidVat === 'function') {
      return await window.FicooProAjax.proCallForValidVat(vatNumber, countryCode, section, ficooCheckbox);
    }
    return await coreCallForValidVat();
  }

  /**
   *  Check if Pro AJAX is available, otherwise use core functions.
   */
  async callForInvalidVat() {
    if (typeof window.FicooProAjax !== 'undefined' &&
      typeof window.FicooProAjax.proCallForInvalidVat === 'function') {
      return await window.FicooProAjax.proCallForInvalidVat();
    }
    return await coreCallForInvalidVat();
  }


  /**
   *  Check if Pro AJAX is available, otherwise use core functions.
   */
  async callForEmptyVat() {
    if (typeof window.FicooProAjax !== 'undefined' &&
      typeof window.FicooProAjax.proCallForEmptyVat === 'function') {
      return await window.FicooProAjax.proCallForEmptyVat();
    }
    return await coreCallForEmptyVat();
  }

  /**
 * Validate VAT number on page load.
 */
  async validateVatNumberOnLoad() {

    logger.info("START Validazione partita IVA al caricamento della pagina");

    const vatInput = ficooInput.vat;
    if (!vatInput) {
      logger.p("VAT input non trovato");
      return true;
    }

    return await this.handleValidationVat();

  }

  hasVisibleFicooErrors() {

    // Check ficoo errors by ids
    const ficooErrorIds = {
      "ficoo-invalid-field-vat": __("Invalid VAT number", "ficoo-smart-connector-core"),
      "ficoo-invalid-field-taxcode": __("Invalid tax code", "ficoo-smart-connector-core"),
      "ficoo-invalid-field-pec": __("Invalid PEC address", "ficoo-smart-connector-core"),
      "ficoo-invalid-field-reccode": __("Invalid recipient code", "ficoo-smart-connector-core"),
    };
    // get all elements by id from ficooErrorIds
    const ficooErrors = Object.keys(ficooErrorIds)
      .map(id => document.getElementById(id))
      .filter(el => el !== null);

    // if any error are present create a new div and append it INSIDE the <fieldset id="shipping-fields">
    // the div contain texts of all errors present
    if (ficooErrors.length > 0) {
      // remove existing error summary if exists
      let existingErrorDiv = document.getElementById("ficoo-error-summary");
      if (existingErrorDiv) {
        existingErrorDiv.remove();
      }
      let errorDiv = document.getElementById("ficoo-error-summary");
      if (!errorDiv) {
        errorDiv = document.createElement("div");
        errorDiv.id = "ficoo-error-summary";
        errorDiv.style.border = "2px solid #cd0e0e";
        errorDiv.style.backgroundColor = "#f8d7da";
        errorDiv.style.color = "#721c24";
        errorDiv.style.padding = "10px";
        errorDiv.style.marginBottom = "10px";
        errorDiv.style.borderRadius = "5px";

        // deermine where to insert the div
        targetCountry = this.getTargetCountry();
        if ('shipping-country' === targetCountry.id) {

          // insert insiede <fieldset id="shipping-fields"> BEFORE <div class="wc-block-components-checkout-step__container">
          const shippingFieldset = document.getElementById("shipping-fields");
          const containerDiv = shippingFieldset.querySelector(".wc-block-components-checkout-step__container");
          if (shippingFieldset && containerDiv) {
            shippingFieldset.insertBefore(errorDiv, containerDiv);
          }
        } else if ('billing-country' === targetCountry.id) {
          // insert above billing fields
          const billingFieldset = document.getElementById("billing-fields");
          const containerDiv = billingFieldset.querySelector(".wc-block-components-checkout-step__container");
          if (billingFieldset && containerDiv) {
            billingFieldset.insertBefore(errorDiv, containerDiv);
          }
        } else {
          // insert insiede <fieldset id="shipping-fields"> BEFORE <div class="wc-block-components-checkout-step__container">
          const shippingFieldset = document.getElementById("shipping-fields");
          const containerDiv = shippingFieldset.querySelector(".wc-block-components-checkout-step__container");
          if (shippingFieldset && containerDiv) {
            shippingFieldset.insertBefore(errorDiv, containerDiv);
          }
        }
      }

      // Clear previous errors.
      errorDiv.innerHTML = "";

      // Add current errors.
      ficooErrors.forEach(el => {
        const errorText = ficooErrorIds[el.id] || "Errore di validazione";
        const p = document.createElement("p");
        p.style.marginBottom = "0";
        p.innerText = errorText;
        errorDiv.appendChild(p);
      });
    } else {
      // Remove error summary if no errors.
      const existingErrorDiv = document.getElementById("ficoo-error-summary");
      if (existingErrorDiv) {
        existingErrorDiv.remove();
      }
    }

    console.log("ficooErrors by IDs:", ficooErrors);

    // return true if any of the ficooErrors are visible
    if (ficooErrors.some(el => el.offsetParent !== null && !el.closest('[style*="display: none"]'))) {
      logger.info(`Ficoo specific visible errors found by IDs: ${ficooErrors.length}`);
      ficooErrors.forEach((el, index) => {
        logger.p(`Ficoo visible error by ID ${index + 1}:`, el);
        //LOG ELEMENT ID IF EXISTS.
        if (el.id) {
          logger.p(`Ficoo visible error by ID ${index + 1} ID:`, el.id);
        }
      });
      return true;
    }

  }

  /**
   * Setup submit button validation.
   */
  setupSubmitValidation() {
    const validateBeforeSubmit = () => {
      // Add listener for place order button as backup.
      const addSubmitButtonListener = () => {
        const placeOrderButton = document.querySelector(
          ".wc-block-components-checkout-place-order-button"
        );

        if (placeOrderButton) {
          placeOrderButton.addEventListener(
            "click",
            (event) => {
              if (this.hasVisibleFicooErrors()) {
                logger.info(
                  "Clic sul pulsante bloccato: errori di validazione presenti"
                );
                event.preventDefault();
                event.stopPropagation();

                // Scroll to error.
                const firstError =
                  document.querySelector(
                    ".wc-block-components-validation-error"
                  ) || document.querySelector(".has-error");
                if (firstError) {
                  firstError.scrollIntoView({
                    behavior: "smooth",
                    block: "center",
                  });
                }

                return false;
              }
            },
            true
          ); // Use capturing to intercept event before default listener.
          logger.info("Listener del pulsante di invio ordine configurato");
        } else {
          // If button is not available yet, retry later.
          setTimeout(addSubmitButtonListener, 500);
        }
      };

      // Start button listener.
      addSubmitButtonListener();

      logger.info("Validazione pre-invio ordine configurata");
    };

    // Execute validation setup.
    validateBeforeSubmit();
  }
}


// Initialize the handler.
const ficooCheckoutHandler = new FicooCheckoutHandler();

// Export for potential use by other modules.
// export default ficooCheckoutHandler;